export const BUSINESS_TIME_ZONE = 'Asia/Shanghai';

interface BusinessTimeParts {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
}

function parseEventTime(value: unknown): Date | null {
  if (typeof value !== 'string' && !(value instanceof Date)) return null;
  const raw = value instanceof Date ? value.toISOString() : value.trim();
  if (!raw) return null;
  if (!(value instanceof Date) && !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function businessParts(value: unknown): BusinessTimeParts | null {
  const parsed = parseEventTime(value);
  if (!parsed) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(parsed);
  const valueOf = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  return {
    year: valueOf('year'),
    month: valueOf('month'),
    day: valueOf('day'),
    hour: valueOf('hour'),
    minute: valueOf('minute'),
    second: valueOf('second'),
  };
}

export function formatBusinessDateTime(value: unknown, options: { seconds?: boolean; fallback?: string } = {}): string {
  const parts = businessParts(value);
  if (!parts) return options.fallback ?? '—';
  const base = `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
  return options.seconds === false ? base : `${base}:${parts.second}`;
}

export function formatBusinessMonthDayTime(value: unknown, fallback = '—'): string {
  const parts = businessParts(value);
  if (!parts) return fallback;
  return `${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
}

export function formatBusinessTime(value: unknown, fallback = '—'): string {
  const parts = businessParts(value);
  if (!parts) return fallback;
  return `${parts.hour}:${parts.minute}`;
}

export function formatBusinessDate(value: unknown, fallback = '—'): string {
  const parts = businessParts(value);
  if (!parts) return fallback;
  return `${parts.year}-${parts.month}-${parts.day}`;
}

export function businessDateKey(value: unknown): string {
  return formatBusinessDate(value, '');
}
